import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  FileText,
  Search,
  TrendingUp,
  FileCheck,
  AlertTriangle,
  Mic,
  Languages,
  Users2,
  ArrowRight,
  Zap,
  BarChart3,
  Shield,
  Clock,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AILanding = () => {
  const navigate = useNavigate();

  const aiCards = [
    {
      id: 'ai-insights',
      title: 'AI Insights',
      description: 'Intelligent summaries, risk scoring & anomaly detection',
      icon: <Brain size={24} />,
      path: '/ai/insights',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Smart summaries', 'Risk scoring', 'Anomaly detection'],
    },
    {
      id: 'ai-contract-drafting',
      title: 'AI Contract Drafting',
      description: 'Smart contract drafting from natural language prompts',
      icon: <FileText size={24} />,
      path: '/ai/drafting',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Smart drafting', 'Natural language', 'Template generation'],
    },
    {
      id: 'ai-search-qa',
      title: 'AI Search & Q&A',
      description: 'Natural language search & intelligent queries',
      icon: <Search size={24} />,
      path: '/ai/search',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Natural language', 'Smart search', 'Q&A system'],
    },
    {
      id: 'predictive-analytics',
      title: 'Predictive Analytics',
      description: 'AI-powered renewal & dispute predictions',
      icon: <TrendingUp size={24} />,
      path: '/ai/analytics',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Renewal predictions', 'Dispute forecasting', 'Risk analysis'],
    },
    {
      id: 'document-ingestion',
      title: 'Document Ingestion',
      description: 'OCR & NLP metadata extraction from documents',
      icon: <FileCheck size={24} />,
      path: '/ai/ingestion',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['OCR processing', 'NLP extraction', 'Metadata analysis'],
    },
    {
      id: 'clause-risk-scoring',
      title: 'Clause Risk Scoring',
      description: 'AI-powered risk analysis & clause assessment',
      icon: <AlertTriangle size={24} />,
      path: '/ai/risk-scoring',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Risk analysis', 'Clause assessment', 'Compliance check'],
    },
    {
      id: 'voice-to-contract',
      title: 'Voice-to-Contract',
      description: 'Speech-to-contract generation & processing',
      icon: <Mic size={24} />,
      path: '/ai/voice-contract',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: [
        'Speech recognition',
        'Voice processing',
        'Contract generation',
      ],
    },
    {
      id: 'multilingual-translation',
      title: 'Multilingual Translation',
      description: 'Automatic contract translation & localization',
      icon: <Languages size={24} />,
      path: '/ai/translation',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Auto-translation', 'Localization', 'Multi-language'],
    },
    {
      id: 'ai-negotiation',
      title: 'AI Negotiation Support',
      description: 'Intelligent clause suggestions & negotiation strategies',
      icon: <Users2 size={24} />,
      path: '/ai/negotiation',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: [
        'Clause suggestions',
        'Negotiation strategies',
        'AI recommendations',
      ],
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <div className="page-container">
      <PageBreadcrumb />
      <PageHeader
        title="AI & Intelligence"
        description="Advanced AI-powered Contract LifeCycle Management capabilities"
      />

      {/* AI Management Cards */}
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <h3 className="text-modern-title mb-0">
              AI & Intelligence Modules
            </h3>
            <p className="text-modern-description mb-0">
              Choose an AI module to leverage advanced intelligence capabilities
            </p>
          </div>
          <div className="card-body">
            <div className="grid-modern-3">
              {aiCards.map((card) => (
                <div
                  key={card.id}
                  className="asset-landing-card"
                  onClick={() => handleCardClick(card.path)}
                >
                  {/* Top Accent Line */}
                  <div
                    style={{
                      height: '4px',
                      background: 'var(--primary-color)',
                      width: '100%',
                    }}
                  />

                  {/* Card Content */}
                  <div style={{ padding: '18px' }}>
                    {/* Icon and Title */}
                    <div className="d-flex align-items-center mb-2">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: card.iconBg || '#D6EFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '16px',
                        }}
                      >
                        <div
                          style={{ color: card.iconLine || '#2D7DD2' }}
                          className="asset-card-icon"
                        >
                          {card.icon}
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4
                          style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: '0',
                            fontFamily: 'var(--font-family)',
                          }}
                        >
                          {card.title}
                        </h4>
                      </div>
                      <ArrowRight size={20} className="asset-card-arrow" />
                    </div>

                    {/* Description */}
                    <p
                      style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        margin: '0 0 12px 0',
                        lineHeight: '1.4',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {card.description}
                    </p>

                    {/* Features */}
                    <div>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '6px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Key Features
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        {card.features.map((feature, index) => (
                          <span
                            key={index}
                            style={{
                              fontSize: '11px',
                              padding: '3px 6px',
                              background: '#f3f4f6',
                              color: '#374151',
                              borderRadius: '4px',
                              fontWeight: '500',
                            }}
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="page-content mt-4">
        <div className="card">
          <div className="card-header">
            <h3 className="text-modern-title mb-0">AI-Powered Intelligence</h3>
          </div>
          <div className="card-body">
            <div className="grid-modern-2">
              <div>
                <h5
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '12px',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  <Zap
                    size={20}
                    style={{ color: '#F59E0B', marginRight: '8px' }}
                  />
                  Advanced AI Capabilities
                </h5>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5',
                  }}
                >
                  Leverage cutting-edge AI for contract analysis, risk
                  assessment, predictive analytics, and intelligent automation
                  to streamline your operations.
                </p>
              </div>

              <div>
                <h5
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '12px',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  <Brain
                    size={20}
                    style={{ color: '#8B5CF6', marginRight: '8px' }}
                  />
                  Intelligent Insights
                </h5>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5',
                  }}
                >
                  Get intelligent insights, automated risk scoring, anomaly
                  detection, and predictive analytics to make data-driven
                  decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AILanding;
